// 图片生成工具 - 多接口自动切换

const buildEnhancedPrompt = (prompt: string, style: string): string => {
  let enhancedPrompt = prompt.trim();
  if (style === 'realistic') {
    enhancedPrompt += ', photorealistic, ultra high quality, detailed, professional photography, 8k resolution';
  } else if (style === 'anime') {
    enhancedPrompt += ', anime style, japanese animation, colorful, vibrant, manga art';
  } else if (style === 'artistic') {
    enhancedPrompt += ', oil painting, impressionist style, canvas texture, fine art masterpiece';
  } else if (style === 'digital') {
    enhancedPrompt += ', digital art, concept art, futuristic, sci-fi, detailed, neon lights';
  }
  return enhancedPrompt;
};

const getDimensions = (aspectRatio: string): { width: number; height: number } => {
  if (aspectRatio === '16:9') return { width: 1280, height: 720 };
  if (aspectRatio === '9:16') return { width: 720, height: 1280 };
  return { width: 1024, height: 1024 };
};

// 验证图片 URL 是否可访问（返回直接 URL，不转 blob，避免 CORS）
const verifyImageUrl = async (url: string, timeoutMs: number): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const timer = setTimeout(() => {
      img.src = '';
      reject(new Error('图片加载超时'));
    }, timeoutMs);

    img.onload = () => {
      clearTimeout(timer);
      resolve(url);
    };
    img.onerror = () => {
      clearTimeout(timer);
      reject(new Error('图片加载失败'));
    };
    img.src = url;
  });
};

// 接口1：Pollinations flux
const generateWithPollinationsFlux = (prompt: string, width: number, height: number): Promise<string> => {
  const seed = Math.floor(Math.random() * 999999);
  const url = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=${width}&height=${height}&seed=${seed}&nologo=true&model=flux`;
  return verifyImageUrl(url, 55000);
};

// 接口2：Pollinations turbo
const generateWithPollinationsTurbo = (prompt: string, width: number, height: number): Promise<string> => {
  const seed = Math.floor(Math.random() * 999999);
  const url = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=${width}&height=${height}&seed=${seed}&nologo=true&model=turbo`;
  return verifyImageUrl(url, 50000);
};

// 接口3：Pollinations flux-schnell
const generateWithPollinationsSchnell = (prompt: string, width: number, height: number): Promise<string> => {
  const seed = Math.floor(Math.random() * 999999);
  const url = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=${width}&height=${height}&seed=${seed}&nologo=true&model=flux-schnell`;
  return verifyImageUrl(url, 45000);
};

// 接口4：Pollinations p16（备用）
const generateWithPollinationsP16 = (prompt: string, width: number, height: number): Promise<string> => {
  const seed = Math.floor(Math.random() * 999999);
  const url = `https://image.pollinations.ai/prompt/${encodeURIComponent(prompt)}?width=${width}&height=${height}&seed=${seed}&nologo=true&model=p16`;
  return verifyImageUrl(url, 60000);
};

// 接口5：Readdy AI 搜索图片（最终兜底）
const generateWithReaddySearch = (prompt: string, width: number, height: number): Promise<string> => {
  let orientation = 'squarish';
  if (width > height) orientation = 'landscape';
  else if (height > width) orientation = 'portrait';
  const seq = Math.floor(Math.random() * 9000) + 1000;
  const url = `https://public.readdy.ai/ai/img_res/948a13f1eb402f8ff16472e217608fb6.jpg${encodeURIComponent(prompt)}&width=${width}&height=${height}&seq=${seq}&orientation=${orientation}`;
  return verifyImageUrl(url, 30000);
};

// 带超时的 Promise 竞速（忽略失败，只取第一个成功）
const raceWithTimeout = (promises: Promise<string>[]): Promise<string | null> =>
  Promise.any(promises).catch(() => null);

export const generateImageWithGemini = async (
  prompt: string,
  style: string,
  aspectRatio: string
): Promise<string> => {
  const enhancedPrompt = buildEnhancedPrompt(prompt, style);
  const { width, height } = getDimensions(aspectRatio);

  // 第一轮：同时发起 flux + turbo + schnell，谁先成功用谁
  const round1 = await raceWithTimeout([
    generateWithPollinationsFlux(enhancedPrompt, width, height),
    generateWithPollinationsTurbo(enhancedPrompt, width, height),
    generateWithPollinationsSchnell(enhancedPrompt, width, height),
  ]);
  if (round1) return round1;

  // 第二轮：换备用模型 p16
  const round2 = await raceWithTimeout([
    generateWithPollinationsP16(enhancedPrompt, width, height),
  ]);
  if (round2) return round2;

  // 最终兜底：Readdy 搜索图片
  const round3 = await raceWithTimeout([
    generateWithReaddySearch(prompt, width, height),
  ]);
  if (round3) return round3;

  throw new Error('图片生成失败，请稍后重试');
};
